import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { gql, useQuery } from '@apollo/client'
import { format, isAfter, isBefore, startOfDay } from 'date-fns'
import { slugs } from '@/constants/slugs'
import { useExperimentContext } from '@/experimentation'
import { createWebClient } from '@/services/ApolloClient'
import { TheatricalShowtimesByDayQuery, TheatricalShowtimesByDayQueryVariables } from '@/types/codegen-federation'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeAnalytics } from '@/utils/analytics'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getObjectFromLocalStorage } from '@/utils/local-storage'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { removeFromSessionStorage } from '@/utils/session-storage'
import { useTranslate } from '@/utils/translate/translate-client'
import { useTicketsContext } from '@/views/TicketCheckoutViews/ShowtimesView/components/TicketsContext'
import { useAddressSearch } from '@/views/TicketCheckoutViews/ShowtimesView/hooks/useAddressSearch'
import {
  getEarliestDate,
  isDateExpired,
  parseQueryDate,
  saveCoordinates,
  saveSearchDate,
} from '@/views/TicketCheckoutViews/ShowtimesView/hooks/useVenueSearch/venueSearchUtils'
import { isQueryCoordinates } from '@/views/TicketCheckoutViews/ShowtimesView/utils/utils'
import { useTheatricalPromotionContext } from '@/views/TicketCheckoutViews/components/TheatricalPromotionContext'

export interface Coordinates {
  latitude: number
  longitude: number
}

export interface SavedSearchStateType {
  date: Date | null
  coordinates: Coordinates | null
  searchString: string | null
  expires: Date
}

export type ShowtimesVenue = NonNullable<TheatricalShowtimesByDayQuery['theatricalShowtimesByDay']>['venues'][number]

export const theatricalShowtimesByDay = gql`
  query theatricalShowtimesByDay($input: TheatricalShowtimesByDayInput!) {
    theatricalShowtimesByDay(input: $input) {
      error
      searchLocation {
        city
      }
      venues {
        id
        name
        url
        distance
        vendorId
        providerId
        isPremiumPartner
        isPresaleCompatible
        presaleGoal {
          ticketsSold
          nextMilestone
        }
        vendorLogo {
          url
          cloudinaryPath
        }
        phone
        ticketingProviderSlug
        addressText
        address {
          line
          city
          countryCode
          zipCode
        }
        showtimes {
          availableInventory
          id
          isNew
          language
          localStartTimeFriendly
          localTimeOffset
          providerId
          releaseType
          reservable
          seatmap {
            cols
            rows
            seats {
              available
              x
              y
            }
          }
          subtitleLanguage
          ticketingId
          ticketUrl
          totalSeats
          utcStartTime
        }
      }
    }
  }
`
const atomOnlyFlows = new Set(['guild', 'angel', 'preferred'])

interface Props {
  releaseDate: Date
  earlyDateToTry?: Date
  theatricalSlug: string
  countryCode: string
  onVenueChange?: () => void
  hasCalled?: boolean
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const useVenueSearch = ({ releaseDate, earlyDateToTry, theatricalSlug, onVenueChange, countryCode }: Props) => {
  const { track } = useSafeAnalytics()
  const { locale } = useLocale()
  const { query, isReady, replace, pathname } = useSafeRouter()
  const { search } = useAddressSearch({ countryCode })
  const { isFreeTicketPromo } = useTicketsContext()
  const { t } = useTranslate('tickets')
  const { getFeatureValue } = useExperimentContext()
  const { promo } = useTheatricalPromotionContext()

  // A/B test: control group lands on official release date, treatment group lands on early release date
  // Early date is always available for display, experiment only controls initial landing
  // Feature flag check is deferred until we confirm venues exist on early date to ensure exposures
  // only happen for users who actually have venues with showtimes on the early date

  const [searchDate, setSearchDate] = useState<Date>(() => getEarliestDate(releaseDate))
  const searchDateRef = useRef<Date>(searchDate)
  const isInitializedRef = useRef(false)
  const queryDateRef = useRef<string | undefined>(undefined)
  const [coordinates, setCoordinates] = useState<Coordinates | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const [hasSearchedOnce, setHasSearchedOnce] = useState(false)
  const [defaultAddressString, setDefaultAddressString] = useState('')
  const [hasDeterminedSpecialDate, setHasDeterminedSpecialDate] = useState(false)

  const allowedVendorIds = useMemo(() => {
    const queryVendorIds = getItemOrFirstEntry(query?.vendorIds)

    if (queryVendorIds) return new Set(queryVendorIds.toLowerCase().split(','))
    return null
  }, [query?.vendorIds])

  const searchStorageKey = `angel-search-state-${theatricalSlug}`

  const shouldShowAtomVenuesOnly = useMemo(() => {
    return (
      query?.shouldShowAtomVenuesOnly === 'true' ||
      atomOnlyFlows?.has((query?.flow as string) ?? '') ||
      !!query?.discounts
    )
  }, [query?.discounts, query?.flow, query?.shouldShowAtomVenuesOnly])

  const client = useMemo(() => {
    return createWebClient({ locale })
  }, [locale])

  const formattedSearchDate = format(searchDate ?? new Date(), 'yyyy-MM-dd')
  const { data, loading, error } = useQuery<TheatricalShowtimesByDayQuery, TheatricalShowtimesByDayQueryVariables>(
    theatricalShowtimesByDay,
    {
      variables: {
        input: {
          theatricalSlug,
          date: formattedSearchDate,
          freeTicketCompatibleVenuesOnly: isFreeTicketPromo,
          includeVenuesWithoutShowtimes: shouldShowAtomVenuesOnly ? false : !isFreeTicketPromo,
          coords: coordinates,
          ticketingProviderSlug: shouldShowAtomVenuesOnly ? 'ATOM' : null,
        },
      },
      skip: !isInitialized || !searchDate || !client,
      onCompleted: (data) => {
        const venues = data?.theatricalShowtimesByDay?.venues
        if (venues) setHasSearchedOnce(true)
        const venuesWithShowtimes = venues?.filter((venue) => venue?.showtimes?.length > 0)
        track('Ticket Venues Searched', {
          venues: venues?.map((venue) => ({
            id: venue?.id,
            showtimes: venue?.showtimes?.map((showtime) => ({
              id: showtime?.id,
            })),
          })),
          showtimesFound: venuesWithShowtimes && venuesWithShowtimes.length > 0,
        })
      },
      client,
      errorPolicy: 'all',
    },
  )

  const formattedEarlyDateToTry = format(earlyDateToTry ?? new Date(), 'yyyy-MM-dd')
  const earlyDateToTryIsValid = useMemo(() => {
    return earlyDateToTry && !isDateExpired(earlyDateToTry)
  }, [earlyDateToTry])

  const earlyDateToTryQuerySkip = useMemo(() => {
    return !isInitialized || !earlyDateToTryIsValid || !client
  }, [isInitialized, earlyDateToTryIsValid, client])

  const { data: earlyDateToTryData, loading: earlyDateToTryLoading } = useQuery<
    TheatricalShowtimesByDayQuery,
    TheatricalShowtimesByDayQueryVariables
  >(theatricalShowtimesByDay, {
    variables: {
      input: {
        theatricalSlug,
        date: formattedEarlyDateToTry,
        freeTicketCompatibleVenuesOnly: isFreeTicketPromo,
        includeVenuesWithoutShowtimes: false,
        coords: coordinates,
        ticketingProviderSlug: 'ATOM',
      },
    },
    skip: earlyDateToTryQuerySkip,
    client,
    errorPolicy: 'all',
    onCompleted: (data) => {
      // Query completed - we now know whether specialDate exists or not
      setHasDeterminedSpecialDate(true)

      // Update date when specialDate becomes available after initialization
      if (isInitializedRef.current && !queryDateRef.current) {
        const hasVenuesWithFutureShowtimes = data?.theatricalShowtimesByDay?.venues?.some((venue) =>
          venue?.showtimes?.some((showtime) => new Date(showtime?.utcStartTime) > new Date()),
        )

        const currentSearchDate = searchDateRef.current

        // Check for saved search state first to avoid experiment exposure if user has a valid saved date
        const savedSearchState = getObjectFromLocalStorage<SavedSearchStateType>(searchStorageKey)
        const hasValidSavedDate =
          savedSearchState?.date &&
          !isDateExpired(savedSearchState?.expires) &&
          new Date(savedSearchState.date) >= getEarliestDate(earlyDateToTry ?? releaseDate)

        // Only proceed with experiment logic if venues with future showtimes exist, early date is available, and no valid saved date exists
        // This ensures exposures only happen for users who actually have venues with showtimes on the early date
        // and don't have a saved preference
        if (hasVenuesWithFutureShowtimes && earlyDateToTry && !hasValidSavedDate) {
          const featureFlagEnabled = getFeatureValue('land-on-early-release-date', false)

          if (featureFlagEnabled) {
            // Special date is available and experiment enabled - update if still on default release date
            const isOnDefaultReleaseDate =
              format(currentSearchDate, 'yyyy-MM-dd') === format(getEarliestDate(releaseDate), 'yyyy-MM-dd')
            const isNotOnSpecialDate = format(currentSearchDate, 'yyyy-MM-dd') !== format(earlyDateToTry, 'yyyy-MM-dd')

            if (isOnDefaultReleaseDate && isNotOnSpecialDate) {
              setSearchDate(earlyDateToTry)
            }
          }
        } else if (earlyDateToTry && !hasVenuesWithFutureShowtimes) {
          // No venues with future showtimes found - move off early date if currently on it
          const isOnEarlyDate = format(currentSearchDate, 'yyyy-MM-dd') === format(earlyDateToTry, 'yyyy-MM-dd')

          if (isOnEarlyDate) {
            setSearchDate(getEarliestDate(releaseDate))
          }
        }
      }
    },
  })
  // Keep refs in sync with state
  useEffect(() => {
    searchDateRef.current = searchDate
    isInitializedRef.current = isInitialized
    queryDateRef.current = query?.date as string | undefined
  }, [searchDate, isInitialized, query?.date])

  useEffect(() => {
    const ipCity = data?.theatricalShowtimesByDay?.searchLocation?.city
    if (ipCity) {
      const newAddressString = t('ipCityShowtimes', '{{ipCity}} Showtimes', { ipCity })
      setDefaultAddressString(newAddressString)
    }
  }, [data?.theatricalShowtimesByDay?.searchLocation?.city, t])

  useEffect(() => {
    if (data?.theatricalShowtimesByDay?.error === 'IP_LOCATION_NOT_FOUND' && window?.navigator?.geolocation) {
      window.navigator.geolocation.getCurrentPosition(
        (geoLocation) =>
          setCoordinates({ latitude: geoLocation.coords.latitude, longitude: geoLocation.coords.longitude }),
        () => {
          setHasSearchedOnce(true)
        },
      )
    }
  }, [data?.theatricalShowtimesByDay?.error])

  const setDefaultLocation = useCallback(async () => {
    if (query?.location) {
      const queryLocation = query?.location as string
      const isCoordinates = isQueryCoordinates(queryLocation)

      if (isCoordinates) {
        const [latitude, longitude] = queryLocation.split(',')
        return setCoordinates({ latitude: parseFloat(latitude ?? ''), longitude: parseFloat(longitude ?? '') })
      }

      const searchResult = await search(queryLocation)
      if (searchResult?.geometry?.coordinates) {
        const locationLabel = searchResult?.properties?.label
        setDefaultAddressString(locationLabel)
        return setCoordinates(searchResult.geometry.coordinates)
      }
    }

    const savedSearchState = getObjectFromLocalStorage<SavedSearchStateType>(searchStorageKey)

    if (savedSearchState && savedSearchState?.coordinates) {
      if (savedSearchState?.searchString) {
        setDefaultAddressString(savedSearchState.searchString)
      }
      if (!isDateExpired(savedSearchState?.expires)) return setCoordinates(savedSearchState.coordinates)
    }
  }, [query?.location, search, searchStorageKey])

  const specialDate = useMemo(() => {
    const hasVenuesWithFutureShowtimes = earlyDateToTryData?.theatricalShowtimesByDay?.venues?.some((venue) =>
      venue?.showtimes?.some((showtime) => new Date(showtime?.utcStartTime) > new Date()),
    )
    return hasVenuesWithFutureShowtimes && earlyDateToTry ? earlyDateToTry : undefined
  }, [earlyDateToTryData?.theatricalShowtimesByDay?.venues, earlyDateToTry])

  // Track when we've determined specialDate to avoid flicker
  useEffect(() => {
    // No early date to check - we're done immediately
    if (!earlyDateToTryIsValid) {
      setHasDeterminedSpecialDate(true)
      return
    }
    // Not initialized yet - still waiting
    if (!isInitialized) {
      setHasDeterminedSpecialDate(false)
      return
    }
    // Query is skipped - we won't get an answer, so we're done
    // (onCompleted won't fire when query is skipped)
    if (earlyDateToTryQuerySkip) {
      setHasDeterminedSpecialDate(true)
    }
  }, [earlyDateToTryIsValid, isInitialized, earlyDateToTryQuerySkip])

  // Show calendar skeleton if we have earlyDateToTry but haven't determined specialDate yet
  const isCalendarLoading = useMemo(() => {
    if (!earlyDateToTryIsValid) return false
    return !hasDeterminedSpecialDate
  }, [earlyDateToTryIsValid, hasDeterminedSpecialDate])

  const setDefaultDate = useCallback(() => {
    // Don't set date if we're still loading earlyDateToTry data - wait for it to complete
    // This prevents race conditions where we set a date before specialDate is determined
    if (earlyDateToTryIsValid && earlyDateToTryLoading) return

    const queryDate = query?.date && parseQueryDate(query?.date as string)

    if (queryDate) {
      const isQueryInPast = isAfter(startOfDay(new Date()), startOfDay(queryDate))
      if (!isQueryInPast) return setSearchDate(queryDate)
    }

    const savedSearchState = getObjectFromLocalStorage<SavedSearchStateType>(searchStorageKey)

    if (savedSearchState && savedSearchState.date) {
      if (
        !isDateExpired(savedSearchState?.expires) &&
        new Date(savedSearchState.date) >= getEarliestDate(earlyDateToTry ?? releaseDate)
      ) {
        return setSearchDate(new Date(savedSearchState.date))
      }
    }

    // If sing-along David promo and before Dec 31, set initial date to Dec 31 because
    // that's when the earliest sing-alongs are available
    const currentDate = new Date()
    const december31 = new Date(2025, 11, 31)
    if (theatricalSlug === slugs.david && promo?.id === 'SINGALONG' && isBefore(currentDate, december31)) {
      return setSearchDate(december31)
    }

    setSearchDate(getEarliestDate(releaseDate))
  }, [
    query?.date,
    releaseDate,
    searchStorageKey,
    earlyDateToTry,
    earlyDateToTryIsValid,
    earlyDateToTryLoading,
    theatricalSlug,
    promo?.id,
  ])

  const setupDefaultSearchParameters = useCallback(async () => {
    setDefaultDate()
    await setDefaultLocation()
    setIsInitialized(true)
  }, [setDefaultDate, setDefaultLocation])

  useEffect(() => {
    if (!isReady || isInitialized) return

    setupDefaultSearchParameters()
  }, [isReady, isInitialized, setupDefaultSearchParameters])

  const removeQueryParam = useCallback(
    (param: string) => {
      if (!query?.[param]) return

      delete query[param]
      replace(
        {
          pathname: pathname,
          query: query,
        },
        undefined,
        { shallow: true },
      )
    },
    [pathname, query, replace],
  )

  const handleAddressChange = useCallback(
    (coordinates: Coordinates, searchString: string) => {
      const venues =
        data?.theatricalShowtimesByDay?.venues?.map((venue) => ({
          id: venue?.id,
          showtimes: venue?.showtimes?.map((showtime) => ({
            id: showtime?.id,
          })),
        })) ?? []
      track('Tickets Search Submitted', {
        date: searchDate,
        coordinates,
        theatricalSlug: theatricalSlug,
        venues,
      })
      removeFromSessionStorage('venueId')
      setCoordinates(coordinates)
      saveCoordinates(coordinates, searchString, searchStorageKey)
      removeQueryParam('location')
      onVenueChange?.()
    },
    [
      data?.theatricalShowtimesByDay?.venues,
      onVenueChange,
      removeQueryParam,
      searchDate,
      searchStorageKey,
      theatricalSlug,
      track,
    ],
  )

  const handleDateChange = useCallback(
    (date: Date) => {
      track('Tickets Search Submitted', {
        date,
        coordinates,
        theatricalSlug: theatricalSlug,
      })
      setSearchDate(date)
      saveSearchDate(date, searchStorageKey)
      removeQueryParam('date')
      onVenueChange?.()
    },
    [coordinates, onVenueChange, removeQueryParam, searchStorageKey, theatricalSlug, track],
  )

  return useMemo(() => {
    const venues = data?.theatricalShowtimesByDay?.venues ?? []

    const filteredVenues = venues?.filter((venue) => {
      if (allowedVendorIds) return allowedVendorIds.has(venue?.vendorId?.toLowerCase() ?? '')
      return true
    })

    return {
      earliestDate: getEarliestDate(releaseDate),
      specialDate,
      searchDate,
      handleDateChange,
      handleAddressChange,
      defaultAddressString,
      loading,
      error,
      venues: filteredVenues,
      hasSearchedOnce,
      vendorIdsToDisplay: allowedVendorIds,
      earlyDateToTryLoading,
      isCalendarLoading,
    }
  }, [
    data?.theatricalShowtimesByDay?.venues,
    releaseDate,
    searchDate,
    handleDateChange,
    handleAddressChange,
    defaultAddressString,
    loading,
    error,
    hasSearchedOnce,
    allowedVendorIds,
    specialDate,
    earlyDateToTryLoading,
    isCalendarLoading,
  ])
}
